import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import './RoleSelection.css';

const RoleSelection = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [selectedRole, setSelectedRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check if user already has a role selected
  useEffect(() => {
    const checkUserRole = async () => {
      console.log('🎯 RoleSelection: Checking if user has role...', {
        isAuthenticated,
        hasUser: !!user,
        currentPath: window.location.pathname
      });
      
      if (isAuthenticated && user) {
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.user && data.user.role) {
              // User already has a role, redirect to main app
              console.log('User already has role:', data.user.role, '- redirecting to main app');
              navigate('/');
              return;
            }
          }
        } catch (err) {
          console.log('User has no role yet, showing role selection');
        }
      }
    };

    checkUserRole();
  }, [isAuthenticated, user, getAccessTokenSilently, navigate]);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      setError('Please select a role to continue');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = await getAccessTokenSilently();
              const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/select-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role: selectedRole,
          name: user?.name || user?.email?.split('@')[0] || 'User',
          email: user?.email || ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (selectedRole === 'counselor') {
          // Redirect to counselor verification page
          navigate('/counselor-verification');
        } else {
          // For all other roles, redirect to main app
          navigate('/');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to select role');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return <div>Please log in to continue.</div>;
  }

  return (
    <div className="role-selection-container">
      <div className="role-selection-card">
        <div className="role-selection-header">
          <div className="logo-container">
            <img 
              src="/[NEW]Quila_Logo.png" 
              alt="Quila Logo" 
              className="quila-logo"
            />
          </div>
          <h1>Welcome to Quila!</h1>
          <p>Which role best describes you?</p>
        </div>

        <div className="role-options">
          <div className="role-section">
            <h2 className="section-title">Available Now</h2>
            <div className="active-roles">
              <div 
                className={`role-option ${selectedRole === 'student' ? 'selected' : ''}`}
                onClick={() => handleRoleSelect('student')}
              >
                <div className="role-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="role-content">
                  <h3>Student</h3>
                  <p>I'm a student looking to apply to college</p>
                  <ul>
                    <li>Track your GPA, SAT/ACT scores</li>
                    <li>Manage extracurricular activities</li>
                    <li>Explore colleges and programs</li>
                    <li>Plan your application timeline</li>
                  </ul>
                </div>
              </div>

              <div 
                className={`role-option ${selectedRole === 'counselor' ? 'selected' : ''}`}
                onClick={() => handleRoleSelect('counselor')}
              >
                <div className="role-icon">
                  <img 
                    src="/icons8-mentor-50.png" 
                    alt="Counselor/Mentor" 
                    width="32" 
                    height="32"
                    style={{ filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(199deg) brightness(101%) contrast(101%)' }}
                  />
                </div>
                <div className="role-content">
                  <h3>Counselor/Mentor</h3>
                  <p>I'm a counselor or mentor helping students with college applications</p>
                  <ul>
                    <li>Manage student portfolios</li>
                    <li>Track application progress</li>
                    <li>Provide guidance and recommendations</li>
                    <li>Access student analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="role-section">
            <h2 className="section-title">Coming Soon</h2>
            <div className="coming-soon-roles">
              <div 
                className={`role-option coming-soon ${selectedRole === 'district_student' ? 'selected' : ''}`}
                onClick={() => handleRoleSelect('district_student')}
              >
                <div className="role-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="role-content">
                  <h3>District Student</h3>
                  <p>I'm a student in a school district using Quila</p>
                  <ul>
                    <li>Access district-specific resources</li>
                    <li>Connect with district counselors</li>
                    <li>Use district-approved tools</li>
                    <li>Follow district guidelines</li>
                  </ul>
                </div>
              </div>

              <div 
                className={`role-option coming-soon ${selectedRole === 'district_admin' ? 'selected' : ''}`}
                onClick={() => handleRoleSelect('district_admin')}
              >
                <div className="role-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 7V3H2V21H22V7H12M6 19H4V17H6V19M6 15H4V13H6V15M6 11H4V9H6V11M6 7H4V5H6V7M10 19H8V17H10V19M10 15H8V13H10V15M10 11H8V9H10V11M10 7H8V5H10V7M20 19H12V17H14V15H12V13H14V11H12V9H20V19Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="role-content">
                  <h3>District Administrator</h3>
                  <p>I manage counselors and oversee district operations</p>
                  <ul>
                    <li>Manage counselor accounts</li>
                    <li>Review counselor verifications</li>
                    <li>Access district-wide analytics</li>
                    <li>Configure system settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="role-selection-actions">
          <button 
            className={`continue-button ${selectedRole ? 'active' : 'disabled'}`}
            onClick={handleSubmit}
            disabled={!selectedRole || isSubmitting}
          >
            {isSubmitting ? 'Setting up...' : 'Continue'}
          </button>
        </div>

        <div className="role-selection-footer">
          <p>You can change your role later in your account settings</p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelection; 